import React from 'react';
import styled from '@emotion/styled';
import { LayoutList, ArrowLeft } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import { lensDisplayName, lensImageUrl } from '../../utils/lensDisplay';
import LensCard from './LensCard';
import PivotContextLine from './PivotContextLine';
import { gradientText } from '../../styles/mixins';

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  color: white;
`;

const HeaderBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 32px 0;
  max-width: 900px;
  margin: 0 auto;
  width: 100%;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover { background: rgba(255, 255, 255, 0.1); color: #fff; }
`;

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 1.3rem;
  font-weight: 700;
  ${gradientText('#ffaa00', '#c084fc', '#ffaa00')}
`;

const SummaryButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.25);
    color: #fff;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  padding: 32px 32px 16px;
  max-width: 900px;
  margin: 0 auto;
  width: 100%;
  flex: 1;
  align-content: center;

  @media (max-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 14px;
    padding: 20px 16px 16px;
  }
`;

/**
 * LensGridView - 3x2 grid of lens selection cards (Screen 3).
 */
export default function LensGridView({
  lenses = [],
  itemCounts = {},
  pivotPointTitle = '',
  onSelectLens,
  onShowPivotSummary,
  onBack,
}) {
  const { t, language } = useTranslation();

  const showHeader = onBack || pivotPointTitle || onShowPivotSummary;

  return (
    <Container>
      {showHeader && (
        <HeaderBar>
          {onBack && (
            <BackButton onClick={onBack}>
              <ArrowLeft size={16} />
              {t('lenses.pivotPointsNav')}
            </BackButton>
          )}
          <TitleBlock>
            <PageTitle>{t('lenses.stepLenses')}</PageTitle>
            <PivotContextLine
              title={pivotPointTitle}
              label={t('lenses.pivotLabel')}
              compact
            />
          </TitleBlock>
          {onShowPivotSummary && (
            <SummaryButton onClick={onShowPivotSummary}>
              <LayoutList size={14} />
              {t('lenses.pivotSummaryBtn')}
            </SummaryButton>
          )}
        </HeaderBar>
      )}
      <Grid>
        {lenses.map((lens, lensIndex) => {
          const counts = itemCounts[lens.id] || {};
          return (
            <LensCard
              key={lens.id}
              name={lensDisplayName(lens, language)}
              imageUrl={lensImageUrl(lens, lensIndex)}
              color={lens.color}
              obstacleCount={counts.obstacles || 0}
              opportunityCount={counts.opportunities || 0}
              onClick={() => onSelectLens(lens)}
            />
          );
        })}
      </Grid>
    </Container>
  );
}
